import { inject, injectable } from 'inversify'
import { cloneDeep } from 'lodash-es'
import { nanoid } from 'nanoid'
import { Float32BufferAttribute, Group, type Line, Vector3, Vector4 } from 'three'
import { NURBSCurve } from 'three/examples/jsm/curves/NURBSCurve.js'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import { SplineEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { SplineUtils } from '@/plugins/cad/module/utils'
import { DrawUtils, type IPoint } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'

const splineMachine = setup({
  actions: {
    showMouseTip: (..._args: any[]) => {},
  },
}).createMachine({
  id: 'drawSplineTool',
  initial: 'idle',
  states: {
    idle: { on: { NEXT: 'specifyStartPoint' } },
    specifyStartPoint: {
      entry: { type: 'showMouseTip', params: CadTipKey.draw.firstPoint },
      on: { NEXT: { target: 'specifyNextPoint' } },
    },
    specifyNextPoint: {
      entry: { type: 'showMouseTip', params: CadTipKey.draw.nextPoint },
      on: { NEXT: {} },
    },
  },
})

/**
 * 样条曲线绘制工具，连续拾取控制点生成平滑样条。
 */
@injectable()
export class DrawSplineTool extends StateMachineTool<typeof splineMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawSplineTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = splineMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private splineObject: Line2 | null = null
  private previewLineObject: Line | null = null
  private entity: SplineEntity | null = null
  private currentCommand: DrawEntityCommand | null = null

  protected onInitialize(entityData?: Partial<SplineEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.updateEntity()
    this.historyManager.commitCommand()
    this.splineObject && DrawUtils.disposeObject(this.splineObject)
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.splineObject = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
  }

  /**
   * 鼠标按下：左键追加控制点，右键结束绘制。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
      this.exit()
    }
  }

  /**
   * 鼠标移动：更新末端控制点预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }

    const [p1, p2] = points.slice(-2)
    this.updateSplineObject(points)
    this.updatePreviewLine(p1, p2)
  }

  private updateSplineObject(points: IPoint[]) {
    if (points.length < 2 || !this.entity) return

    const splineParams = SplineUtils.fitPointsToSpline(
      points.map((p) => ({ x: p.x, y: p.y })),
      this.entity?.degree
    )
    let sampledPoints: Vector3[] = []
    if (points.length === 2) {
      // 仅两个点时，绘制直线
      const p1 = new Vector3(points[0].x, points[0].y, 0)
      const p2 = new Vector3(points[1].x, points[1].y, 0)
      sampledPoints = [p1, p2]
    } else {
      // 多于两个点时，使用样条曲线拟合
      const controlPoints = splineParams.controlPoints.map((pt) => new Vector4(pt.x, pt.y, 0, 1))
      const nurbsCurve = new NURBSCurve(splineParams.degree, splineParams.knots, controlPoints)

      // 使用统一采样规则，保持与最终渲染阶段一致
      const divisions = SplineUtils.getSampleCount(points.length)
      sampledPoints = nurbsCurve.getPoints(divisions)
    }
    const geometry = DrawUtils.getLineGeometryByPoints(sampledPoints)
    if (!this.splineObject) {
      this.splineObject = createCadLine2Object(
        geometry,
        MaterialUtils.createMaterialByLineType(
          this.entity.lineType,
          MaterialUtils.getMaterialOptionByEntity(this.entity)
        )
      )
      this.splineObject.computeLineDistances()
      this.group.add(this.splineObject)
      return
    }
    this.splineObject.geometry.dispose()
    this.splineObject.geometry = geometry
    this.splineObject.computeLineDistances()
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(Setting.dashSize)
      const materialOption = {
        color: Setting.leadLineConfig.color,
        transparent: Setting.leadLineConfig.opacity < 1,
        opacity: Setting.leadLineConfig.opacity,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedLine(p1, p2, materialOption, this.group)
      return
    }

    const positions = [p1, p2].flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<SplineEntity>): SplineEntity {
    const entity = new SplineEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
      method: 'FIT',
      degree: 3,
    })
    return entity
  }

  private updateEntity() {
    if (!this.entity || this.points.length < 2) {
      return
    }
    // 根据当前的拟合点数据计算样条参数
    const splineParams = SplineUtils.fitPointsToSpline(
      this.points.map((p) => ({ x: p.x, y: p.y })),
      this.entity.degree
    )
    Object.assign(this.entity, {
      ...cloneDeep(splineParams),
      fitPoints: this.points.map((p) => ({ x: p.x, y: p.y })),
    })
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    return this.entity?.controlPoints ? this.entity.controlPoints.length >= 2 : false
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }
}
